import useApi from '~/composables/useApi';
import { FilterInput, ProductVersion, FormInput, Dimming, SelectedValues } from './types';
import stepOneQuery from './query/stepOne.gql';
import stepThreeQuery from './query/stepThree.gql';
import stepFiveQuery from './query/stepFive.gql';
import { Logger } from '~/helpers/logger';
import saveFormDataQuery from './query/saveForm.gql';
import lensesAndCriQuery from './query/lensesAndCri.gql';
import colorTemperatureQuery from './query/colorTemperature.gql';
import fluxRangeQuery from './query/fluxRange.gql';
import dimmingQuery from './query/dimming.gql';
import consoleQuery from './query/console.gql';
import ledModuleTypeQuery from './query/ledModuleType.gql';
import luminaireConfigFilters from './query/luminaireConfigFilters.gql';
import luminaireConfigsAvailability from './query/luminaireConfigsAvailability.gql';
import ldtOrIesFile from './query/ldtOrIesFile.gql';
import attributeInfo from './query/attributeInfo.gql';
import resetForm from './query/resetForm.gql';
import article from './query/article.gql';
import driverSizeQuery from './query/driverSize.gql';
import moment from 'moment';
import axios, { ResponseType } from 'axios';
import dataHelpers from './dataHelpers';
import { useCustomerStore } from '~/stores/customer';

import {
	StepOne,
	StepTwo,
	StepThree,
	BaseAttribute,
	FluxRange,
	LedModuleType,
	LuminaireConfigAvailability,
	FilePath,
	LuminaireConfigFilters,
	ColorTemperature
} from '~/modules/vizulo/productConfigurator/types';
import {
	useContext,
	ref,
	Ref,
	reactive
} from '@nuxtjs/composition-api';
import { json } from 'stream/consumers';
import { FixedProductTaxDisplaySettings } from '~/../api-client/lib';

interface ProductConfiguratorOutput {
    productVersions: Array<ProductVersion>,
    formData: String,
}

const errorsFactory = () => ({
	submitForm: null,
});

const error: Ref = ref(errorsFactory());

const productConfigurator:any = ref({
	step_1: {
		consoles: [],
		productVersions: [],
		mountings: [],
	},
	step_2: {
		lenses: [],
		cri: [],
		colorTemperatures: [],
		lensApplicationGroups: [],
		ledModuleCategories: [],
		distributionTypes: [],
		ledDensity: [
			{
				name: 'low',
				id: 18,
			},
			{
				name: 'high',
				id: 20,
			},
		]
	},
	step_3: {
		insulations: [],
		socketLocations: [],
		dimming: [],
		surgeProtection: [],
	},
	step_4: {
		moduleTypes: [],
		driverFeatures: [],
		filters: {
			maxDiodeCurrent: 0,
			nominalPowerFactor: 0,
			maxSurgeProtectionLN: 8,
			maxSurgeProtectionLN_PE: 10,
			minSurgeProtection: 2,
			minDriverPowerUtilization: 40,
			maxDriverPowerUtilization: 80,
			ranks: [],
			diodeQuantity: [],
		},
	},
	step_5: {
		colors: [],
		defaultColors: [],
		colorsRAL: [],
		featureSets: [],
		layers: [],
		mountingScrewLength: {},
	},
	step_6: {
		downloads: [
			'Datasheet',
			'LDT file',
			'IES file'
		]
	}
});

const selectedValues = ref({
	photometricConfig: {},
	configuratorMode: 'basic',
	general: {
		activeStep: 'step_1',
		selectedDiode: null,
		availableDiodes: null,
		productSku: 0,
		beeProductId: 0,
		productName: '',
		productApprovals: '',
		product: null,
		luminaireConfigs:[],
		filteredLuminaireConfigs: [],
		reloadLuminaireConfigs: false,
		modelNamePatterns: [],
		modelName: [],
		ledQuantity: 0,
		chosenConfiguration: null,
		formIsComplete: false,
		attributeInfo: {},
		application: '',
		userHasChangedFilters: false,
		article: '',
		editMode: false,
		inProcessOfLogin: false,
		asanaReportSent: false,
		diode: null,
		productDescription: '',
		bodyMaterial: '',
		coverMaterial: '',
		opticMaterial: '',
		ip_rating: 0,
		ik_rating: 0,
		attributesSelectedByUser: {},
		unfinishedItems: [],
		formDataLogged: false,
		userStory: [],
	},
	step_1: {
		productVersions: {},
		console: null,
		formIsLoaded: false,
		isComplete: false,
		versionsAreEdited: false,
		mounting: null,
		consolesLoaded: false,
	},
	step_2: {
		formIsLoaded: false,
		lens: null,
		cri: null,
		colorTemperature: null,
		constantLightOutput: false,
		initialOutput: 0,
		lifeTime: '',
		fluxPower: {
			selected: 'flux',
			power: 0,
			flux: 0
		},
		minFlux: 0,
		maxFlux: 0,
		minPower: 0,
		maxPower: 0,
		originalMaxPower: 0,
		originalMaxFlux: 0,
		isComplete: false,
		typicalValues: {
			colorTemperature: [],
			cri: [],
		},
		LEDCategoryName: '',
	},
	step_3: {
		insulation: null,
		surgeProtection: false,
		thermalProtectionNTC: false,
		driverSize: [],
		sockets: {},
		socketOption: null,
		dimming: null,
		isComplete: false,
		configCount: 0,
		scheduleIsSet: false,
		dimmingParameters: {
			midnight: {
				steps: {
					1: {
						outputLevel: 100,
						setTime: ''
					},
					2: {
						outputLevel: 50,
						setTime: {
							HH: '23',
							mm: '00'
						}
					},
					3: {
						outputLevel: 100,
						setTime: {
							HH: '05',
							mm: '00',
						}
					},
					4: {
						outputLevel: '',
						setTime: {
							HH: null,
							mm: null,
						}
					},
					5: {
						outputLevel: '',
						setTime: {
							HH: null,
							mm: null,
						}
					},
					6: {
						outputLevel: 0,
						setTime: null
					}
				}
			},
			step: {
				switchOnLevel: 50,
				SDLevel: 100,
			},
			mains: {
				minLevel: 50,
				maxLevel: 100,
				minVoltage: 180,
				maxVoltage: 230,
			}
		}
	},
	step_4: {
		diodeQuantity: [],
		isComplete: false,
		ranks: [],
		binId: 0,
		powerFactor: 0,
		luminousEfficacy: 0,
		driverPowerUtilization: 70,
		diodeCurrent: 0,
		surgeProtectionLN: 6,
		surgeProtectionPE: 6,
		ledModuleTypes: [],
		maxLedsPerModule: 0,
		minLuminousEfficacy: 100,
		maxLuminousEfficacy: 150,
		luminaireConfigPreference: 1,
		preferenceChanged: false,
		activeFilters: {
			current: false,
			surgeProtection: true,
			driverRank: true,
			minPowerFactor: false,
			minLuminousEfficacy: false,
			driverPowerUtilization: true,
			diodeQuantity: false,
			ledModuleType: false,
		}
	},
	step_5: {
		color: null,
		layer: null,
		mountingScrewLength: null,
		isComplete: false,
	},
	step_6: {
		ldtFilePath: null,
		iesFilePath: null,
		filesAvailable: [],
		productImagePath: null,
		tenderText: '',
		dimensions: {},
		versionNumber:'',
		projectReference: '',
	},
});

const resetErrorValue = () => {
	error.value = errorsFactory();
};

const loading = ref({
	attributes: false,
	steps: true,
	moduleType: false,
});

export const useProductConfigurator = () => {
	const { query, mutate } = useApi();
	const { app } = useContext();
	const customerStore = useCustomerStore();

	const {
		sendAsanaBugReport,
		getProductVersionNames,
		getProductVersionIds,
		prepareTenderData,
		getSelectedLedModuleId,
		getSocketLocations,
		prepareDriverFeatures,
		checkIfStepIsCompleted,
		setPrioritizedDiode,
		getSessionId,
		getFormData,
		saveFormData,
	} = dataHelpers();

	const currentStep = ref('');
	const initialLoad = ref(false);
	let valueIsChanged = false;
	const disabledDimmingIds = [];
	const preSetPowerUtilization = 70;
	const ecoProductVersionId = 68;
	const noDimmingIds = [70, 71];
	let moduleTypeIds = ref([]);

	const relatedAttributes = {
		'productVersions': ['lenses', 'console', 'resetStepTwo'],
		'mounting': ['lenses', 'console', 'resetStepTwo'],
		'cri': ['colorTemperature', 'fluxRange'],
		'colorTemperature': ['fluxRange'],
		'sockets': ['dimming'],
		'lens': ['colorTemperature', 'fluxRange'],
		'console' : ['resetStepTwo'],
		'power': ['general'],
		'flux': ['general'],
		'initialOutput': ['setFluxPower'],
		'constantLightOutput': ['setFluxPower'],
	}

	const safeModelNameAttributes = ['Mounting', 'Console', 'Color rendering index', 'Color temperature', 'Lens', 'LED quantity', 'Color', 'Insulation', 'LED module type'];
	const importantStepsInEditMode = ['step_1', 'step_2', 'step_3'];

	const getProductConfigurator = async (productSku: string, isAuthenticated: boolean): Promise<any> => {
		const sessionData = await getFormData(productSku);
		prepareSteps(isAuthenticated, sessionData);
		checkSessionState(productSku, sessionData);
		const versionSelected = selectedValues.value.step_1.versionsAreEdited ? getProductVersionIds(selectedValues.value.step_1.productVersions) : [];

		const filter = {
			product_sku: productSku ?? selectedValues.value.general.productSku,
			product_version_ids: versionSelected,
		};

		initialLoad.value = true;
		const { data } = await query<{ stepOne: StepOne}>(stepOneQuery, { filter });

		getAttributeInfo();

		if (data.stepOne.productVersions) {
			if (data?.stepOne.product && data?.stepOne.product.beeProductId) {
				selectedValues.value.general.beeProductId = data?.stepOne.product.beeProductId;
				selectedValues.value.general.productName = data?.stepOne.product.productName;
				selectedValues.value.general.productApprovals = data?.stepOne.product.productApprovals.join(', ');
				selectedValues.value.general.productDescription = data?.stepOne.product.productDescription;
				selectedValues.value.general.bodyMaterial = data?.stepOne.product.bodyMaterial;
				selectedValues.value.general.coverMaterial = data?.stepOne.product.coverMaterial;
				selectedValues.value.general.opticMaterial = data?.stepOne.product.opticMaterial;
				selectedValues.value.general.ip_rating = data?.stepOne.product.ipRating;
				selectedValues.value.general.ik_rating = data?.stepOne.product.ikRating;
			}

			setProductVersions(data);
			setMountings(data.stepOne.mountings);

			productConfigurator.value.step_1 = data?.stepOne ?? [];

			if (data?.stepOne.productVersions && data?.stepOne.productVersions.length > 0) {
				selectedValues.value.general.application = data?.stepOne.productVersions[0].application;
			}
		}

		loading.value.steps  = false;

		if (data) {
			loading.value.attributes = false;
		}
	};

	const setProductVersions = (data) => {
		data?.stepOne.productVersions.forEach(selectedVersion => {
			if (selectedVersion.id === ecoProductVersionId &&  data.stepOne.standardVersionName && data.stepOne.echoVersionName) {
				selectedVersion['configurator_name'] = data.stepOne.echoVersionName;
				selectedVersion['configurator_comparison_name'] = data.stepOne.standardVersionName;
			}

			if (selectedVersion.pre_selected_by_user) {
				if (selectedValues.value.step_1.productVersions[selectedVersion.id]) {
					selectedVersion['selected'] = selectedValues.value.step_1.productVersions[selectedVersion.id].selected;
				} else {
					selectedVersion['selected'] = true;
				}

				selectedValues.value.step_1.productVersions[selectedVersion.id] = selectedVersion;
			}

			if (!selectedVersion.pre_selected_by_user && selectedVersion.is_pre_selected) {
				if (selectedValues.value.step_1.productVersions[selectedVersion.id]) {
					selectedVersion['selected'] = selectedValues.value.step_1.productVersions[selectedVersion.id].selected;
				} else {
					selectedVersion['selected'] = false;
				}

				selectedValues.value.step_1.productVersions[selectedVersion.id] = selectedVersion;
			}
		});
	}

	const isMountingVersionSelected = () => {
		const mountingVersion = productConfigurator.value.step_1.productVersions.find(obj => obj.id == 65);

		if (mountingVersion) {
			if (selectedValues.value.step_1.productVersions.hasOwnProperty('65')) {
				getConsoles();
			} else {
				return;
			}
		}

		getConsoles();
	}

	const setMountings = (mountings) => {
		if (mountings && !selectedValues.value.step_1.mounting) {
			mountings.forEach(mounting => {
				if (mounting.pre_selected_by_user) {
					selectedValues.value.step_1.mounting = mounting;
					selectedValues.value.step_1.isComplete = true;
				}
			});
		}
	}

	const getStepThree = async (storeCode): Promise<any> => {
		try {
			loading.value.steps = true;
			let sockets = {};

			Object.entries( selectedValues.value.step_3.sockets).forEach(entry => {
				const [key, value] = entry;

				if (value['id'] !== 0) {
					sockets[key] = value['id'];
				}
			});

			const filter = {
				bee_product_id: selectedValues.value.general.beeProductId,
				sockets: JSON.stringify(sockets),
				mounting_id: selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
			}

			await query<{ stepThree: StepThree}>(stepThreeQuery, { filter }).then(({ data })=> {
				productConfigurator.value.step_3.insulations = data.stepThree.insulations;
				productConfigurator.value.step_3.socketLocations = data.stepThree.socket_locations;
				productConfigurator.value.step_3.surgeProtection = data.stepThree.surge_protection;
				productConfigurator.value.step_3.dimming = data.stepThree.dimming.filter((item) => {
					return !disabledDimmingIds.includes(item.id);
				});

				if (selectedValues.value.general.modelNamePatterns && selectedValues.value.general.modelNamePatterns.length === 0) {
					selectedValues.value.general.modelNamePatterns = data.stepThree.model_name_pattern;
					updateSelectedValues('general', 'modelNamePatterns', data.stepThree.model_name_pattern);
				}

				if (selectedValues.value.step_3.dimming === null || selectedValues.value.step_3.dimming.id === 70) {
					let dimming = data.stepThree.dimming.find((item)=> {
						return item.id === 70;
					})
					let socketEmpty = false;

					if (Object.keys(selectedValues.value.step_3.sockets).length > 0) {
						for (const socket in selectedValues.value.step_3.sockets) {
							if (selectedValues.value.step_3.sockets[socket] && selectedValues.value.step_3.sockets[socket].id === 0) {
								socketEmpty = true;
							}
						}

						if (!socketEmpty) {
							selectedValues.value.step_3.dimming = dimming;
						}
					}

					productConfigurator.value.step_3.socketLocations.forEach(element => {
						if(!selectedValues.value.step_3.sockets[element.name]) {
							selectedValues.value.step_3.sockets[element.name] = { name: 'none', id:0 };
						}
					});
				}

				if (storeCode === 'us') {
					const insulation = productConfigurator.value.step_3.insulations.find((item) => {
						return item.id == 45;
					})

					selectedValues.value.step_3.insulation = insulation;
					updateSelectedValues('step_3', 'insulation', selectedValues.value.step_3.insulation);
				}

				if (data) {
					loading.value.attributes = false;
				}
			});

			loading.value.steps = false;
		} catch (err) {
			error.value.saveForm = err;
			Logger.error('ProductConfigurator/stepThree', err);
			loading.value.steps = false;
		}
	}

	const driverSize = async () => {
		let sockets = {};
		Object.entries( selectedValues.value.step_3.sockets).forEach(entry => {
			const [key, value] = entry;

			if (value['id'] !== 0) {
				sockets[key] = value['id'];
			}
		});

		const filter = {
			bee_product_id: selectedValues.value.general.beeProductId,
			sockets: JSON.stringify(sockets),
			surge_protection: selectedValues.value.step_3.surgeProtection
		};

		const { data } = await query<{ driverSize: Array<String>}>(driverSizeQuery, { filter });

		selectedValues.value.step_3.driverSize = data.driverSize;
	}

	const setSocketLocations = (sockets) => {
		const addedLocations = [];
		sockets.forEach(socket => {
			if (socket.socket_location) {
				socket.socket_location.forEach(location => {
					if (addedLocations.length > 0) {
						if (!addedLocations.includes(location.id)) {
							productConfigurator.value.step_3.socketLocations.push(location);
						}
					} else {
						productConfigurator.value.step_3.socketLocations.push(location);
						addedLocations.push(location.id);
					}
				});
			}
		});
	}

	const prepareSteps = (isAuthenticated, sessionData) => {
		loading.value.steps = true;

		if (sessionData.general) {
			selectedValues.value.general = sessionData.general ?? selectedValues.value.general;
			selectedValues.value.step_1 = sessionData.step_1 ?? selectedValues.value.step_1;
			selectedValues.value.step_2 = sessionData.step_2 ?? selectedValues.value.step_2;
			selectedValues.value.step_3 = sessionData.step_3 ?? selectedValues.value.step_3;
			selectedValues.value.step_4 = sessionData.step_4 ?? selectedValues.value.step_4;
			selectedValues.value.step_5 = sessionData.step_5 ?? selectedValues.value.step_5;
			selectedValues.value.step_6 = sessionData.step_6 ?? selectedValues.value.step_6;

			if (sessionData.inProcessOfLogin) {
				selectedValues.value.general.inProcessOfLogin = sessionData.inProcessOfLogin;
			}
		}

		let saveFormAtTheEnd = false;

		if (sessionData.redirectedToLoginForAdvanceMode && sessionData.redirectedToLoginForAdvanceMode === 'done') {
			window.localStorage.setItem('configuratorMode', 'advanced');
			selectedValues.value.configuratorMode = 'advanced';

			if (selectedValues.value.general.activeStep === 'step_2') {
				getLenses(false).then(() => {
					if (selectedValues.value.step_2.cri !== null) {
						getColorTemperature();
					}
				});
			}

			saveFormAtTheEnd = true;
		}

		if (sessionData.productConfiguratorPhotometryData) {
			selectedValues.value.photometricConfig = sessionData.productConfiguratorPhotometryData.photometricConfig;
		}

		if (isAuthenticated) {
			selectedValues.value.configuratorMode = window.localStorage.getItem('configuratorMode') ?? 'basic';
		} else {
			selectedValues.value.configuratorMode = 'basic';
		}
	}

	const checkSessionState = (productSku, sessionData) => {
		let formComplete = '';
		let sku = '';

		if (sessionData.general) {
			formComplete = sessionData.general.formIsComplete;
			sku = sessionData.general.productSku
		}

		if (formComplete == 'complete' && !selectedValues.value.general.editMode && !selectedValues.value.general.inProcessOfLogin ) {
			resetFormData(false);
		}

		if ( !sku || productSku !== sku.toString()) {
			resetFormData(false);
			selectedValues.value.general.productSku = parseInt(productSku);
		}
	}

	// Gets passed only attributes that are changed and check if related attributes needs updates.
	const checkAttributesToUpdate = async (formData: any, updatedAttribute: string) => {
		const attributesToUpdate = relatedAttributes[updatedAttribute];
		//Sis būs saistībā ar step maiņu atpakaļejoši or smth like that

		if (attributesToUpdate) {
			attributesToUpdate.forEach(attributeName => {
				for (let i in formData) {
					if (formData[i][updatedAttribute] !== undefined) {
						updateAttribute(attributeName);
					}
				}
			});
		} else {
			if (valueIsChanged) {
				loading.value.attributes = false;
			}
		}
	}

	const updateAttribute = (attributeName) => {
		switch (attributeName) {
			case 'lenses':
				selectedValues.value.general.reloadLuminaireConfigs = true;
				selectedValues.value.step_4.ledModuleTypes = [];
				getLenses(true);
				break;
			case 'console':
				selectedValues.value.general.reloadLuminaireConfigs = true;
				if (selectedValues.value.general.activeStep === 'step_1') {
					isMountingVersionSelected();
				}
				break;
			case 'colorTemperature':
				selectedValues.value.general.reloadLuminaireConfigs = true;
				getColorTemperature();
				break
			case 'fluxRange':
				selectedValues.value.step_4.ledModuleTypes = [];
				getFluxRange();
				break
			case 'dimming':
				selectedValues.value.general.reloadLuminaireConfigs = true;
				getDimming();
				break
			case 'setFluxPower':
				setFluxAndPower(0, 0);
				break
			case 'general':
				selectedValues.value.general.reloadLuminaireConfigs = true;
				break
			case 'resetStepTwo':
				selectedValues.value.step_2 = {
					formIsLoaded: false,
					lens: null,
					cri: null,
					colorTemperature: null,
					constantLightOutput: false,
					initialOutput: 0,
					lifeTime: '',
					fluxPower: {
						selected: 'flux',
						power: 0,
						flux: 0
					},
					minFlux: 0,
					maxFlux: 0,
					minPower: 0,
					maxPower: 0,
					originalMaxPower: 0,
					originalMaxFlux: 0,
					isComplete: false,
					typicalValues: {
						colorTemperature: [],
						cri: [],
					},
					LEDCategoryName: '',
				}
				loading.value.attributes = false;
				break
			default:
				break
		}
	}

	const getLenses = async (autoUpdate: boolean = false, hasPresetConfig = false): Promise<any> => {
		if (!autoUpdate || selectedValues.value.step_2.lens !== null) {
			setProductVersionsFromPhotometricConfig(hasPresetConfig);

			const filter = {
				bee_product_id: selectedValues.value.general.beeProductId,
				product_version_ids: getProductVersionIds(selectedValues.value.step_1.productVersions),
				mounting_id: selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
				basic_version: selectedValues.value.configuratorMode === 'basic' ? true : false,
				lens_id: selectedValues.value.photometricConfig['lens_id'] ?? selectedValues.value.step_2.lens?.id ?? null,
			};

			const { data } = await query<{ lensAndCri: StepTwo}>(lensesAndCriQuery, { filter });

			productConfigurator.value.step_2.cri = data.lensAndCri.cri;
			productConfigurator.value.step_2.lenses = data.lensAndCri.lenses;
			productConfigurator.value.step_2.lensApplicationGroups = data.lensAndCri.lens_application_groups;
			productConfigurator.value.step_2.ledModuleCategories = data.lensAndCri.led_module_categories;
			productConfigurator.value.step_2.distributionTypes = data.lensAndCri.optic_types;
			selectedValues.value.step_2.typicalValues.cri = data.lensAndCri.typical_values.colorRenderingIndex;

			if (data.lensAndCri.diode_ids.length > 0) {
				selectedValues.value.general.selectedDiode = setPrioritizedDiode(data.lensAndCri.diode_ids);
				selectedValues.value.general.availableDiodes = data.lensAndCri.diode_ids;
			}

			if (selectedValues.value.photometricConfig.hasOwnProperty('lens_id')) {
				selectedValues.value.step_2.lens = data.lensAndCri.lenses.find((lens) => {
					return lens.id == selectedValues.value.photometricConfig['lens_id'];
				})

				selectedValues.value.step_2.isComplete = true;
				delete selectedValues.value.photometricConfig['lens_id'];
			}

			if (selectedValues.value.photometricConfig.hasOwnProperty('cri_id')) {
				selectedValues.value.step_2.cri = data.lensAndCri.cri.find((cri) => {
					return cri.id == selectedValues.value.photometricConfig['cri_id'];
				})

				selectedValues.value.step_2.isComplete = true;
				delete selectedValues.value.photometricConfig['cri_id'];
			}

			if (selectedValues.value.configuratorMode == 'basic' && selectedValues.value.step_2.cri) {
				if (!selectedValues.value.step_2.typicalValues.cri.includes(selectedValues.value.step_2.cri.id)) {
					selectedValues.value.step_2.typicalValues.cri.push(selectedValues.value.step_2.cri.id);
				}
			}

			if (data) {
				loading.value.attributes = false;
			}
		}
	};



	const getColorTemperature = async (): Promise<any> => {
		if (selectedValues.value.step_2.cri === null) {
			loading.value.attributes = false;
			return;
		}

		const filter = {
			cri_id: selectedValues.value.step_2.cri.id,
			diode_ids: selectedValues.value.general.availableDiodes,
		};

		const { data } = await query<{ colorTemperature: ColorTemperature}>(colorTemperatureQuery, { filter });
		selectedValues.value.step_2.typicalValues.colorTemperature = data.colorTemperature.typical_values.colorTemperature;
		productConfigurator.value.step_2.colorTemperatures = data.colorTemperature.color_temperatures;
		checkAttributesToReset(data.colorTemperature.color_temperatures, 'colorTemperature');

		if (selectedValues.value.photometricConfig.hasOwnProperty('color_temperature_id')) {
			selectedValues.value.step_2.colorTemperature = data.colorTemperature.color_temperatures.find((colorTemperature) => {
				return colorTemperature.id == selectedValues.value.photometricConfig['color_temperature_id'];
			})

			selectedValues.value.step_2.isComplete = true;
			delete selectedValues.value.photometricConfig['color_temperature_id'];
		}

		if (selectedValues.value.configuratorMode == 'basic' && selectedValues.value.step_2.colorTemperature) {
			if (!selectedValues.value.step_2.typicalValues.colorTemperature.includes(selectedValues.value.step_2.colorTemperature.id)) {
				selectedValues.value.step_2.typicalValues.colorTemperature.push(selectedValues.value.step_2.colorTemperature.id);
			}
		}

		if (data) {
			loading.value.attributes = false;
		}
	};

	const getFluxRange = async (saveStepTwo = false): Promise<any> => {
		if (selectedValues.value.step_2.colorTemperature === null) {
			return;
		}

		const filter = {
			cri_id: selectedValues.value.step_2.cri.id,
			color_temperature_id: selectedValues.value.step_2.colorTemperature.id,
			bee_product_id: selectedValues.value.general.beeProductId,
			diode_ids: selectedValues.value.general.availableDiodes,
			product_version_ids: getProductVersionIds(selectedValues.value.step_1.productVersions),
			lens_id: selectedValues.value.step_2.lens.id ?? 0,
			diode_id: selectedValues.value.photometricConfig['diode_id'] ?? 0,
			mounting_id: selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
		};

		const { data } = await query<{ fluxRange: FluxRange}>(fluxRangeQuery, { filter });

		selectedValues.value.step_2.minFlux = data.fluxRange.min_flux;
		selectedValues.value.step_2.minPower = data.fluxRange.min_power;
		selectedValues.value.step_2.originalMaxFlux = data.fluxRange.max_flux;
		selectedValues.value.step_2.originalMaxPower = data.fluxRange.max_power;
		selectedValues.value.general.availableDiodes = data.fluxRange.diode_ids;

		setFluxAndPower(data.fluxRange.max_power, data.fluxRange.max_flux);
		setStep2FromPhotometricConfig(data);

		if (data) {
			loading.value.attributes = false;
		}
	};

	const setProductVersionsFromPhotometricConfig = (hasPresetConfig) => {
		if (hasPresetConfig && selectedValues.value.photometricConfig.hasOwnProperty('product_version_id')) {
			const productVersion = productConfigurator.value.step_1.productVersions.find((version) => {
				return version.id == 68;
			})

			if (selectedValues.value.photometricConfig['product_version_id'] == 68) {
				productVersion['selected'] = true;
			} else {
				productVersion['selected'] = false;
			}

			selectedValues.value.step_1.productVersions[productVersion.id] = productVersion;
			delete selectedValues.value.photometricConfig['product_version_id'];
		}
	}

	const setStep2FromPhotometricConfig = (data) => {
		if (selectedValues.value.photometricConfig.hasOwnProperty('power') && selectedValues.value.photometricConfig['power'] > 0) {
			selectedValues.value.step_2.fluxPower.power = selectedValues.value.photometricConfig['power'];
			selectedValues.value.step_2.fluxPower.selected = 'power';

			delete selectedValues.value.photometricConfig['power'];
		}

		if (selectedValues.value.photometricConfig['luminous_flux'] > 0) {
			selectedValues.value.step_2.fluxPower.flux = selectedValues.value.photometricConfig['luminous_flux'];
			selectedValues.value.step_2.fluxPower.selected = 'flux';

			delete selectedValues.value.photometricConfig['luminous_flux'];
		}

		if (selectedValues.value.photometricConfig.hasOwnProperty('diode_id')) {
			const diodeId = selectedValues.value.photometricConfig['diode_id'];
			selectedValues.value.general.selectedDiode = diodeId ?? 0;
			selectedValues.value.general.availableDiodes = [diodeId];
			delete selectedValues.value.photometricConfig['diode_id'];
		}
	}

	const setFluxAndPower = (maxPower, maxFlux) => {
		if (maxPower === 0 && maxFlux === 0) {
			maxPower = selectedValues.value.step_2.originalMaxPower;
			maxFlux = selectedValues.value.step_2.originalMaxFlux;
		}

		if (selectedValues.value.step_2.constantLightOutput && selectedValues.value.step_2.lifeTime !== '' && selectedValues.value.step_2.initialOutput > 0) {
			selectedValues.value.step_2.maxFlux = Math.floor(maxFlux * selectedValues.value.step_2.initialOutput/100);
			selectedValues.value.step_2.maxPower = Math.floor(maxPower * selectedValues.value.step_2.initialOutput/100);;
		} else {
			selectedValues.value.step_2.maxFlux = maxFlux;
			selectedValues.value.step_2.maxPower = maxPower;
		}

		if (selectedValues.value.step_2.fluxPower.flux > selectedValues.value.step_2.maxFlux) {
			selectedValues.value.step_2.fluxPower.flux = selectedValues.value.step_2.maxFlux
		}

		if (selectedValues.value.step_2.fluxPower.power > selectedValues.value.step_2.maxPower) {
			selectedValues.value.step_2.fluxPower.power = selectedValues.value.step_2.maxPower
		}

		loading.value.attributes = false;
	}

	const getDimming = async (): Promise<any> => {
		let sockets = {};

		Object.entries( selectedValues.value.step_3.sockets).forEach(entry => {
			const [key, value] = entry;
			sockets[key] = value['id'];
		});

		const filter = {
			bee_product_id: selectedValues.value.general.beeProductId,
			sockets: JSON.stringify(sockets),
		};

		const { data } = await query<{ dimming: Array<BaseAttribute>}>(dimmingQuery, { filter });

		productConfigurator.value.step_3.dimming = data.dimming.filter((item) => {
			return !disabledDimmingIds.includes(item.id);
		});

		let socketEmpty = false;

		if (Object.keys(selectedValues.value.step_3.sockets).length > 0) {
			for (const socket in selectedValues.value.step_3.sockets) {
				if (selectedValues.value.step_3.sockets[socket] === 0) {
					if(!socketEmpty) {
						socketEmpty = true;
					}
				}
			}
		}

		if (socketEmpty) {
			selectedValues.value.step_3.dimming = null;
		} else {
			const dimming = {
				id: 70,
				name: 'no dimming',
				mn_label: 'N',
				driver_option_id: null,
			}

			selectedValues.value.step_3.dimming = dimming;
		}

		if (data) {
			loading.value.attributes = false;
		}
	};

	const getConsoles = async (): Promise<any> => {
		const filter = {
			bee_product_id: selectedValues.value.general.beeProductId,
			product_version_ids: getProductVersionIds(selectedValues.value.step_1.productVersions),
		};
		const { data } = await query<{ console: BaseAttribute}>(consoleQuery, { filter });

		productConfigurator.value.step_1.consoles = data.console;
		selectedValues.value.step_1.consolesLoaded = true;
		setConsoles(data.console);
		currentStep.value = 'step_1';

		if (data) {
			loading.value.attributes = false;
		}
	};

	const getLedModuleType = async (): Promise<any> => {
		loading.value.steps = true;

		if (selectedValues.value.step_2.LEDCategoryName?.length) {
			selectedValues.value.step_4.ledModuleTypes = [];
		}

		if (selectedValues.value.step_4.ledModuleTypes.length > 0 && selectedValues.value.step_2.LEDCategoryName === '') {
			return;
		}

		const filter = {
			bee_product_id: selectedValues.value.general.beeProductId,
			lens_id: selectedValues.value.step_2.lens.id,
			mounting_id: selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
			category_name: selectedValues.value.step_2.LEDCategoryName
		};

		productConfigurator.value.step_4.moduleTypes= [];

		const { data } = await query<{ ledModuleType: LedModuleType}>(ledModuleTypeQuery, { filter });
		productConfigurator.value.step_4.moduleTypes = data.ledModuleType;

		productConfigurator.value.step_4.moduleTypes.forEach((item)=> {
			if (item.max_leds_per_module > selectedValues.value.step_4.maxLedsPerModule) {
				selectedValues.value.step_4.maxLedsPerModule = item.max_leds_per_module;
			}
		})

		productConfigurator.value.step_4.moduleTypes.forEach((item)=> {
			if (productConfigurator.value.step_4.moduleTypes.length === 1) {
				item['selected'] = true;
			} else {
				item['selected'] = false;
			}

			selectedValues.value.step_4.ledModuleTypes.push(item);
		})

		if (data) {
			loading.value.attributes = false;
		}
	};

	const setLedModuleBasedOnConfig = () => {
		if (selectedValues.value.general.attributesSelectedByUser['step_4'] && selectedValues.value.general.attributesSelectedByUser['step_4'].includes('ledModuleTypes')) {
			return;
		}

		let availableIds = [];
		if (selectedValues.value.step_4.ledModuleTypes.length > 0 || !selectedValues.value.step_4.activeFilters.ledModuleType) {
			selectedValues.value.general.filteredLuminaireConfigs.forEach((config) => {
				availableIds = [...availableIds, ...config.led_module_ids]
			})
		}

		let availableIdsSet = new Set(availableIds);

		if (availableIdsSet.size === 0) {
			const selectedModule = selectedValues.value.step_4.ledModuleTypes.reduce(function(prev, current) {
				return parseInt(prev.max_leds_per_module) > parseInt(current.max_leds_per_module) ? prev : current;
			});
			selectedModule.selected = true;
		} else {
			const selectedModule = selectedValues.value.step_4.ledModuleTypes.reduce(function(prev, current) {
				const prevInAvailableIds = availableIdsSet.has(prev.id);
				const currentInAvailableIds = availableIdsSet.has(current.id);

				if (prevInAvailableIds && !currentInAvailableIds) {
					return prev;
				} else if (!prevInAvailableIds && currentInAvailableIds) {
					return current;
				} else if (prevInAvailableIds && currentInAvailableIds) {
					return parseInt(prev.max_leds_per_module) > parseInt(current.max_leds_per_module) ? prev : current;
				} else {
					return prev;
				}
			});
			selectedModule.selected = true;
			filterLuminaireConfigs(false, true);
		}
	}

	const getLuminaireConfigFilters = async (storeCode): Promise<any> => {
		if (!selectedValues.value.step_3.dimming) {
			return;
		}

		const filter = {
			diode_ids: selectedValues.value.general.availableDiodes,
		};

		const { data } = await query<{ luminaireConfigFilters: LuminaireConfigFilters}>(luminaireConfigFilters, { filter });
		setStep4Values(data, storeCode);
		loading.value.steps = false;
	};

	const getDiode = async (apiUrl) => {
		await axios({
			url: apiUrl + `productConfigurator/diode/${selectedValues.value.general.selectedDiode}`,
			method: 'GET',
		}).then((res) => {
			selectedValues.value.general.diode = res.data;
		}).catch((err) => {
			console.error('Diode not found!');
		});
	}

	//----------- Data setters -------------
	const getStepFive = async (): Promise<any> => {
		const filter = {
			bee_product_id: selectedValues.value.general.beeProductId,
			mounting_id: selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
			product_version_ids: getProductVersionIds(selectedValues.value.step_1.productVersions),
		};

		const { data } = await query(stepFiveQuery, { filter });

		setColors(data);
		setFeatureSets(data);

		if (data) {
			loading.value.attributes = false;
		}
	};

	const setColors = (data) => {
		const { colors, default_colors } = data.stepFive;

		const ral7035 = default_colors.filter((color) => {
			return color.number == 'RAL 7035';
		})

		const defaultColors = default_colors.filter((color) => {
			return color.number !== 'RAL 7035';
		})

		if ( ral7035.length > 0 ){
			defaultColors.unshift(ral7035[0]);
		}

		productConfigurator.value.step_5.colors = colors.filter((color) => {
			return !color.number.includes('RAL') && (color.number !== 'Anodized' && color.number !== 'Galvanized');
		})

		productConfigurator.value.step_5.colorsRAL = colors.filter((color) => {
			return color.number.includes('RAL') && (color.number !== 'Anodized' && color.number !== 'Galvanized');
		})

		productConfigurator.value.step_5.defaultColors = defaultColors;

		if (!selectedValues.value.step_5.color) {
			const color = defaultColors.find((item) => item.number === 'RAL 7035');
			selectedValues.value.step_5.color = color ?? defaultColors[0] ?? null;
		}
	}

	const setFeatureSets = (data) => {
		const floodConsoleId = 39;
		productConfigurator.value.step_5.layers = data.stepFive.feature_sets[0];
		productConfigurator.value.step_5.mountingScrewLength = {};

		if (selectedValues.value.step_1.console && Number(selectedValues.value.step_1.console.id) !== floodConsoleId) {
			productConfigurator.value.step_5.mountingScrewLength = data.stepFive.feature_sets[1] ?? {};
		}

		if (data.stepFive.feature_sets[0] && data.stepFive.feature_sets[0].feature[0]) {
			selectedValues.value.step_5.layer = data.stepFive.feature_sets[0].feature[0] ?? 0;
		}

		if (data.stepFive.feature_sets[1] && data.stepFive.feature_sets[1].feature[0]) {
			if (selectedValues.value.step_1.console && Number(selectedValues.value.step_1.console.id) !== floodConsoleId) {
				selectedValues.value.step_5.mountingScrewLength = data.stepFive.feature_sets[1].feature[0] ?? null;
			}
		}
	}

	const setStep4Values = (data, storeCode = 'en') => {
		if (selectedValues.value.step_4.diodeCurrent === 0) {
			selectedValues.value.step_4.diodeCurrent = data.luminaireConfigFilters.diode_current;
		}

		if (selectedValues.value.step_4.powerFactor === 0) {
			selectedValues.value.step_4.powerFactor = data.luminaireConfigFilters.power_factor;
			selectedValues.value.step_4.luminousEfficacy = 100;
		}

		if (storeCode === 'us') {
			if (!(selectedValues.value.general.attributesSelectedByUser['step_4'] && selectedValues.value.general.attributesSelectedByUser['step_4'].includes('surgeProtectionLN'))) {
				selectedValues.value.step_4.surgeProtectionLN = 4;
				selectedValues.value.step_4.surgeProtectionPE = 4;
			}
		}

		productConfigurator.value.step_4.filters.nominalPowerFactor = data.luminaireConfigFilters.nominal_power_factor;
		productConfigurator.value.step_4.filters.maxDiodeCurrent = data.luminaireConfigFilters.diode_max_current;
	}

	const setConsoles = (consoles) => {
		if (consoles) {
			let selectedConsoleIsIncluded = false;

			consoles.forEach(element => {
				if (selectedValues.value.step_1.console && element.id === selectedValues.value.step_1.console.id) {
					selectedConsoleIsIncluded = true;
				}
			});

			if (!selectedConsoleIsIncluded) {
				selectedValues.value.step_1.console = null;
			}

			if (consoles.length == 1) {
				selectedValues.value.step_1.console = consoles[0];
			}

			if ( selectedValues.value.step_1.console === null){
				let beeConsole = consoles.find((item) => {
					return item.typical_value == true;
				})

				selectedValues.value.step_1.console = beeConsole ? beeConsole : null;
			}
		}
	}

	const testLuminaireConfigsAvailability = async (luminaireConfigId = 0, partialFiltration = true, storeCode = 'en'): Promise<any> => {
		loading.value.steps = true;
		let sockets = {};

		if (!selectedValues.value.step_3.dimming) {
			const dimming = {
				id: 70,
				name: 'no dimming',
				mn_label: 'N',
				driver_option_id: null,
			}

			selectedValues.value.step_3.dimming = dimming;
		}

		Object.entries( selectedValues.value.step_3.sockets).forEach(entry => {
			const [key, value] = entry;

			if (value['id'] !== 0) {
				sockets[key] = value['id'];
			}
		});

		const preparedData = {
			'productId': selectedValues.value.general.beeProductId,
			'lensId': selectedValues.value.step_2.lens.id ?? 0,
			'diodeIds': selectedValues.value.general.availableDiodes ?? 0,
			'flux': selectedValues.value.step_2.fluxPower.flux ?? 0,
			'power': selectedValues.value.step_2.fluxPower.power ?? 0,
			'colorTemperatureId': selectedValues.value.step_2.colorTemperature.id ?? 0,
			'criId': selectedValues.value.step_2.cri.id ?? 0,
			'productVersionsIds': getProductVersionIds(selectedValues.value.step_1.productVersions) ?? [],
			'driverOptions': getDriverOptions(storeCode),
			'sockets': JSON.stringify(sockets),
			'dimmingId': selectedValues.value.step_3.dimming .id,
			'ledModuleType': getSelectedLedModuleId(selectedValues.value),
			'surgeProtectionDevice': selectedValues.value.step_3.surgeProtection,
			'partialFiltration': partialFiltration,
			'luminaireConfigId': luminaireConfigId,
			'fluxRange': selectedValues.value.step_2.maxFlux - selectedValues.value.step_2.minFlux,
			'powerRange': selectedValues.value.step_2.maxPower - selectedValues.value.step_2.minPower,
			'initialOutput': selectedValues.value.step_2.constantLightOutput ? selectedValues.value.step_2.initialOutput : 0,
			'returnCount': selectedValues.value.general.activeStep == 'step_3',
			'insulationLabel': selectedValues.value.step_3.insulation ? selectedValues.value.step_3.insulation.mn_label : '',
		}

		const filter = {
			form_data: JSON.stringify(preparedData),
		};

		const { data } = await query<{ luminaireConfigAvailability: LuminaireConfigAvailability }>(luminaireConfigsAvailability, { filter });

		if (selectedValues.value.general.activeStep == 'step_3') {
			selectedValues.value.step_3.configCount = data.luminaireConfigAvailability.count;

			if (data) {
				loading.value.attributes = false;
			}
			loading.value.steps = false;

			return;
		}

		if (luminaireConfigId > 0) {
			if (data.luminaireConfigAvailability.items[0]) {
				selectedValues.value.general.ledQuantity = data.luminaireConfigAvailability.items[0].total_diodes;
				selectedValues.value.general.chosenConfiguration = data.luminaireConfigAvailability.items[0];
				selectedValues.value.general.selectedDiode = data.luminaireConfigAvailability.items[0].diode_id;
				selectedValues.value.step_4.binId = data.luminaireConfigAvailability.bin_id;
			}
		} else {
			selectedValues.value.general.luminaireConfigs = data.luminaireConfigAvailability.items;
			productConfigurator.value.step_4.driverFeatures = prepareDriverFeatures(data.luminaireConfigAvailability.driver_features, getDriverOptions(storeCode));


			for (let feature in productConfigurator.value.step_4.driverFeatures) {
				if (productConfigurator.value.step_4.driverFeatures[feature].preSelected && !selectedValues.value.step_4.ranks.includes(productConfigurator.value.step_4.driverFeatures[feature].id)) {
					selectedValues.value.step_4.ranks.push(productConfigurator.value.step_4.driverFeatures[feature].id);
				}
			}

			if(data.luminaireConfigAvailability.min_luminous_efficacy) {
				selectedValues.value.step_4.minLuminousEfficacy = data.luminaireConfigAvailability.min_luminous_efficacy;
				selectedValues.value.step_4.maxLuminousEfficacy = data.luminaireConfigAvailability.max_luminous_efficacy;
				selectedValues.value.step_4.luminousEfficacy = Math.max(selectedValues.value.step_4.luminousEfficacy, data.luminaireConfigAvailability.min_luminous_efficacy);
			}

			selectedValues.value.general.reloadLuminaireConfigs = false;

			if (selectedValues.value.general.luminaireConfigs) {
				console.log('Total configs returned = ' + selectedValues.value.general.luminaireConfigs.length);
			}
		}

		if (data) {
			loading.value.attributes = false;
		}
		loading.value.steps = false;
	};

	const getDriverOptions = (storeCode) => {
		const cloDriverOptionId = 59;
		const thermalProtectionDriveOptionId = 60;
		const UL_ComplientId = 183;
		let options = [];

		if (selectedValues.value.step_2.constantLightOutput) {
			options.push(cloDriverOptionId);
		}

		if (selectedValues.value.step_3.dimming && selectedValues.value.step_3.dimming.driver_option_id) {
			options.push(selectedValues.value.step_3.dimming.driver_option_id);
		}

		if (selectedValues.value.step_3.thermalProtectionNTC) {
			options.push(thermalProtectionDriveOptionId);
		}

		if (storeCode === 'us') {
			options.push(UL_ComplientId);
		}

		if (Object.keys(selectedValues.value.step_3.sockets).length > 0) {
			for (const key in selectedValues.value.step_3.sockets) {
				if (Object.prototype.hasOwnProperty.call(selectedValues.value.step_3.sockets, key)) {
					const element = selectedValues.value.step_3.sockets[key];
					if (element['id'] > 0) {
						options.push(element['driver_option_id']);
					}
				}
			}
		}

		return [...new Set(options)];
	}

	const filterLuminaireConfigs = (ignoreDiodeQuantity = false, ignoreLedModuleFilterNotSelected = false) => {
		selectedValues.value.general.filteredLuminaireConfigs = [];
		moduleTypeIds.value = selectedValues.value.step_4.ledModuleTypes
			.filter(a => a['selected'])
			.map(a => Number(a.id));

		selectedValues.value.general.luminaireConfigs.forEach((config)=>{
			isConfigActive(config, ignoreDiodeQuantity, ignoreLedModuleFilterNotSelected);
		})

		if (selectedValues.value.general.filteredLuminaireConfigs.length === 0 && selectedValues.value.step_4.driverPowerUtilization === preSetPowerUtilization && !selectedValues.value.general.userHasChangedFilters) {
			let step = 10;
			while (productConfigurator.value.step_4.filters.minDriverPowerUtilization <= selectedValues.value.step_4.driverPowerUtilization - step
                && selectedValues.value.general.filteredLuminaireConfigs.length === 0
			) {

				selectedValues.value.step_4.driverPowerUtilization = selectedValues.value.step_4.driverPowerUtilization - step;
				selectedValues.value.general.luminaireConfigs.forEach((config)=>{
					isConfigActive(config, ignoreDiodeQuantity, ignoreLedModuleFilterNotSelected);
				})

				if (selectedValues.value.general.filteredLuminaireConfigs.length === 0) {
					selectedValues.value.general.luminaireConfigs.forEach((config)=>{
						isConfigActive(config, ignoreDiodeQuantity, ignoreLedModuleFilterNotSelected);
					})
				}
			}
		}

		selectedValues.value.general.userHasChangedFilters = true;

		console.log('After filters applied:' + selectedValues.value.general.filteredLuminaireConfigs.length);
	}

	const filterUnavailableModuleTypes = () => {
		productConfigurator.value.step_4.filters.diodeQuantity = [];
		const ledModuleTypeIds = new Set();

		if (selectedValues.value.general.luminaireConfigs && selectedValues.value.general.luminaireConfigs.length > 0){
			selectedValues.value.general.luminaireConfigs.forEach((config)=>{
				if (config.led_module_ids) {
					for (const id of config.led_module_ids) {
						if (!ledModuleTypeIds.has(id)) {
							ledModuleTypeIds.add(id);
						}
					}
				}

				if(!productConfigurator.value.step_4.filters.diodeQuantity.includes(config.total_diodes)) {
					productConfigurator.value.step_4.filters.diodeQuantity.push(config.total_diodes);
				}
			})

			productConfigurator.value.step_4.filters.diodeQuantity.sort((a,b) => a - b);
			selectedValues.value.step_4.ledModuleTypes = selectedValues.value.step_4.ledModuleTypes.filter(module => ledModuleTypeIds.has(parseInt(module.id)));

			if (selectedValues.value.photometricConfig.hasOwnProperty('diode_quantity')) {
				selectedValues.value.step_4.diodeQuantity = productConfigurator.value.step_4.filters.diodeQuantity.filter((item) => {
					return item == Number(selectedValues.value.photometricConfig['diode_quantity']);
				})

				if (selectedValues.value.step_4.diodeQuantity.length > 0) {
					selectedValues.value.step_4.activeFilters.diodeQuantity = true;
					filterLuminaireConfigs();
				}

				delete selectedValues.value.photometricConfig['diode_quantity'];
			}
		}
	}

	const isConfigActive = (config, ignoreDiodeQuantity, ignoreLedModuleFilterNotSelected) => {
		const activeFilters = selectedValues.value.step_4.activeFilters;
		if (config.driver_rank_id !== null) {
			let isActive = true;

			if (activeFilters.diodeQuantity && selectedValues.value.step_4.diodeQuantity.length > 0 && !selectedValues.value.step_4.diodeQuantity.includes(config.total_diodes) && !ignoreDiodeQuantity){
				isActive = false;
			}

			if (activeFilters.driverRank && selectedValues.value.step_4.ranks.length > 0) {
				let result = selectedValues.value.step_4.ranks.every((driverFeatureId) => config.driver_feature_ids.includes(Number(driverFeatureId)));

				if (!result) {
					isActive = false;
				}
			}

			if (activeFilters.surgeProtection && Number(selectedValues.value.step_4.surgeProtectionLN) > config.surge_protection_ln){
				isActive = false;
			}

			if (activeFilters.surgeProtection && Number(selectedValues.value.step_4.surgeProtectionPE) > config.surge_protection_ln_pe){
				isActive = false;
			}

			if ((activeFilters.current && !config.diode_current) || (activeFilters.current && selectedValues.value.step_4.diodeCurrent < config.diode_current)){
				isActive = false;
			}

			if (activeFilters.minPowerFactor && selectedValues.value.step_4.powerFactor >= config.power_factor){
				isActive = false;
			}

			if (activeFilters.minLuminousEfficacy && selectedValues.value.step_4.luminousEfficacy > config.luminous_efficacy){
				isActive = false;
			}

			if ((activeFilters.ledModuleType || ignoreLedModuleFilterNotSelected) && moduleTypeIds.value.length > 0) {
				let result = moduleTypeIds.value.some((moduleId) => config.led_module_ids.includes(Number(moduleId)));

				if (!result) {
					isActive = false;
				}
			}

			if (activeFilters.driverPowerUtilization && selectedValues.value.step_4.driverPowerUtilization/100 > config.driver_power_utilization) {
				isActive = false;
			}

			if (isActive) {
				selectedValues.value.general.filteredLuminaireConfigs.push(config);
			}
		}
	}

	const generateModelName = () => {
		let productVersionIndex = 0;
		selectedValues.value.general.modelName = [];

		for (let pattern in selectedValues.value.general.modelNamePatterns) {
			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'product') {
				selectedValues.value.general.modelName.push(selectedValues.value.general.modelNamePatterns[pattern]['mn_label_placeholder']);
			}

			// For product version adds empty string if Standard version and goes thru all saved Mn_labels and saves values by index.
			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'product version') {
				let objectId = selectedValues.value.general.modelNamePatterns[pattern]['object_id'] ?? 0;

				if (selectedValues.value.step_1.productVersions[objectId] && selectedValues.value.step_1.productVersions[objectId]['selected']) {
					selectedValues.value.general.modelName.push(selectedValues.value.step_1.productVersions[objectId]['mn_label']);
					productVersionIndex++;
				} else {
					if (productVersionIndex === 0){
						selectedValues.value.general.modelName.push('');
						productVersionIndex++;
					}
				}
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'led quantity') {
				let ledCount = String(selectedValues.value.general.chosenConfiguration.total_diodes);

				// Adjust to fit 3 symbols on every value
				if (ledCount.length === 1) {
					ledCount = '00' + ledCount;
				} else if (ledCount.length === 2) {
					ledCount = '0' + ledCount;
				}

				selectedValues.value.general.modelName.push(ledCount);
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'power') {
				let power = String(selectedValues.value.step_2.fluxPower.power);

				if (power == '0') {
					power = String(selectedValues.value.general.chosenConfiguration.power);
				}

				// Adjust to fit 3 symbols on every value
				if (power.length === 1) {
					power = '00' + power;
				} else if (power.length === 2) {
					power = '0' + power;
				}

				selectedValues.value.general.modelName.push(power);
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'surge protection' && selectedValues.value.general.chosenConfiguration) {
				selectedValues.value.general.modelName.push(selectedValues.value.general.chosenConfiguration.surge_protection_mn_label);
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'diffuser') {
				selectedValues.value.general.modelName.push('N');
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'through wiring') {
				selectedValues.value.general.modelName.push('N');
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'dimming') {
				let mnLabel = '';
				let foundLabel = false;

				if (Object.keys(selectedValues.value.step_3.sockets).length > 0) {
					for (const key in selectedValues.value.step_3.sockets) {
						if (Object.prototype.hasOwnProperty.call(selectedValues.value.step_3.sockets, key) && !foundLabel) {
							const element = selectedValues.value.step_3.sockets[key];
							if (element['id'] > 0) {
								mnLabel = setLabelForSocket(element);
								foundLabel = true;
							}
						}
					}
				}

				if (mnLabel) {
					selectedValues.value.general.modelName.push(mnLabel);
				} else {
					selectedValues.value.general.modelName.push(selectedValues.value.step_3.dimming['mn_label']);
				}
			}

			const namePairs = {
				'color temperature': {
					name: 'colorTemperature',
					mn_label: 'mn_label'
				},
				'color rendering index': {
					name: 'cri',
					mn_label: 'mn_label'
				},
				'color': {
					name: 'color',
					mn_label: 'code'
				},
				'lens': {
					name: 'lens',
					mn_label: 'name'
				},
				'led module type': {
					name: 'ledModuleType',
					mn_label: 'name'
				}
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase() === 'led module type') {
				let selectedModule = getSelectedLedModuleId(selectedValues.value, true);
				selectedValues.value.general.modelName.push(selectedModule['name']);
			}

			if (safeModelNameAttributes.includes(selectedValues.value.general.modelNamePatterns[pattern]['title'])) {
				let lowerCaseTitle = selectedValues.value.general.modelNamePatterns[pattern]['title'].toLowerCase();

				for (let step in selectedValues.value) {
					if (namePairs.hasOwnProperty(lowerCaseTitle)) {
						const attributeName = namePairs[lowerCaseTitle].name;
						if (selectedValues.value[step][attributeName]) {
							selectedValues.value.general.modelName.push(selectedValues.value[step][attributeName][namePairs[lowerCaseTitle].mn_label]);
						}
					} else {
						if (selectedValues.value[step][lowerCaseTitle]) {
							selectedValues.value.general.modelName.push(selectedValues.value[step][lowerCaseTitle]['mn_label']);
						}
					}
				}
			}

			if (selectedValues.value.general.modelNamePatterns[pattern]['space'] === '&emsp;') {
				selectedValues.value.general.modelName.push('_');
			}
		}
		let finalModelName = '';
		selectedValues.value.general.modelName.forEach((item) => {
			finalModelName = finalModelName.concat(item);
		})
		updateSelectedValues('general', 'modelName', finalModelName);
	}

	const setLabelForSocket = (element) => {
		if (noDimmingIds.includes(selectedValues.value.step_3.dimming.id)) {
			return element['mn_label'];
		} else {
			return selectedValues.value.step_3.dimming[`mn_label_socket_${element['mn_label'].toLowerCase()}`];
		}
	}

	const getLdtOrIesFile = async (type): Promise<any> => {
		if (!selectedValues.value.step_3.dimming) {
			return;
		}

		const preparedData = {
			'productId': selectedValues.value.general.beeProductId,
			'lensId': selectedValues.value.step_2.lens.id ?? 0,
			'flux': selectedValues.value.general.chosenConfiguration.flux,
			'power': selectedValues.value.general.chosenConfiguration.power,
			'dimmingId': selectedValues.value.step_3.dimming.id ?? 0,
			'ledModuleId': getSelectedLedModuleId(selectedValues.value),
			'totalDiodes': selectedValues.value.general.chosenConfiguration.total_diodes ?? 0,
			'diodeId': selectedValues.value.general.selectedDiode ?? 0,
			'type': type,
		}

		const filter = {
			form_data: JSON.stringify(preparedData),
		};

		const { data } = await query<{ ldtOrIesFile: FilePath }>(ldtOrIesFile, { filter });

		if (type === 'ldt') {
			selectedValues.value.step_6.ldtFilePath = data.ldtOrIesFile.file_path;
		} else {
			selectedValues.value.step_6.iesFilePath = data.ldtOrIesFile.file_path;
		}
	};

	const getAttributeInfo = async (): Promise<any> => {
		let attributeObject = {};
		const { data } = await query<{ attributeInfo: Array<BaseAttribute>}>(attributeInfo);

		data.attributeInfo.forEach(item => {
			attributeObject[item.name.replace(/ /g,'_').toLowerCase()] = item.description.replace(new RegExp('\r?\n','g'), '<br />');
		});

		selectedValues.value.general.attributeInfo = attributeObject;
	};

	const getProductImage = async (apiUrl, storeCode): Promise<any> => {
		let consoleId = selectedValues.value.step_1.console?.id ?? null;

		try {
			const res = await axios({
				url: apiUrl + 'productConfigurator/'
                    + 'productImage?modelName=' + selectedValues.value.general.modelName
                    + '&socketLocations=' + getSocketLocations(selectedValues.value)
                    + '&storeCode=' + storeCode
                    + '&sessionId=' + getSessionId(selectedValues.value.general.productSku.toString()),
				method: 'GET',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});

			if (res.data) {
				selectedValues.value.step_6.dimensions = res.data;

				selectedValues.value.step_6.productImagePath = res.data.imagePath || '';
				selectedValues.value.step_6.versionNumber = res.data.versionNumber ?? '';

				setMinIkAndIpRatings();
				getTenderText(apiUrl, storeCode);
			} else {
				selectedValues.value.step_6.productImagePath = '';
			}
			return 'done';
		} catch (error) {
			console.error('Error fetching product image:', error);
			return 'done';
		}
	};

	const setMinIkAndIpRatings = () => {
		const ipRatings = [selectedValues.value.general.ip_rating ?? 0];
		const ikRatings = [selectedValues.value.general.ik_rating ?? 0];
		const productVersions = selectedValues.value.step_1.productVersions;

		for (let version in productVersions) {
			if (productVersions[version]['selected']){
				ipRatings.push(productVersions[version].ip_rating ?? 0);
				ikRatings.push(productVersions[version].ik_rating ?? 0);
			}
		}

		if (selectedValues.value.step_1.mounting) {
			ipRatings.push(selectedValues.value.step_1.mounting.ip_rating ?? 0);
			ikRatings.push(selectedValues.value.step_1.mounting.ik_rating ?? 0);
		}

		const nonZeroIpRatings = ipRatings.filter(num => num !== 0);
		const nonZeroIkRatings = ikRatings.filter(num => num !== 0);
		selectedValues.value.general.ip_rating = Math.min(...nonZeroIpRatings);
		selectedValues.value.general.ik_rating = Math.min(...nonZeroIkRatings);
	}

	const reserveArticle = async (apiUrl, projectReference = null, storeCode = 'en'): Promise<Object> => {
		if (!selectedValues.value.step_3.dimming) {
			return;
		}
		if (loading.value.steps){
			return;
		}

		selectedValues.value.general.userStory.push('article');
		loading.value.steps = true;
		let modelName = String(selectedValues.value.general.modelName);

		if (selectedValues.value.general.modelName.length > 0) {
			modelName = modelName.replace(/\_/g, ' ');
		}

		const dimmingParameters = prepareDimmingParameters();

		const preparedData = {
			'modelName': modelName,
			'productId': selectedValues.value.general.beeProductId,
			'luminaireConfigId': selectedValues.value.general.chosenConfiguration.id,
			'flux': selectedValues.value.general.chosenConfiguration.flux,
			'power': selectedValues.value.general.chosenConfiguration.power ?? 0,
			'cctId': selectedValues.value.step_2.colorTemperature ? selectedValues.value.step_2.colorTemperature.id : 0,
			'criId': selectedValues.value.step_2.cri ? selectedValues.value.step_2.cri.id : 0,
			'lensId': selectedValues.value.step_2.lens.id ?? 0,
			'ledModuleId': getSelectedLedModuleId(selectedValues.value),
			'totalDiodes': selectedValues.value.general.chosenConfiguration.total_diodes ?? 0,
			'colorId': selectedValues.value.step_5.color.id,
			'dimmingId': selectedValues.value.step_3.dimming ? selectedValues.value.step_3.dimming.id : 0,
			'insulationId': selectedValues.value.step_3.insulation.id,
			'surgeProtectionId': selectedValues.value.general.chosenConfiguration.surge_protection_id ?? null,
			'productVersionIds': getProductVersionIds(selectedValues.value.step_1.productVersions) ?? [],
			'consoleId': selectedValues.value.step_1.console ? selectedValues.value.step_1.console.id : 0,
			'binId': selectedValues.value.step_4.binId ?? 0,
			'mountingScrewLength': selectedValues.value.step_5.mountingScrewLength ? selectedValues.value.step_5.mountingScrewLength.id : 0,
			'layer': selectedValues.value.step_5.layer.id,
			'hasSurgeProtectionDevice': selectedValues.value.step_3.surgeProtection,
			'driverOptions': getDriverOptions(storeCode),
			'sockets': selectedValues.value.step_3.sockets,
			'projectReference': projectReference,
			'ledQuantity': selectedValues.value.general.chosenConfiguration.total_diodes,
			'initialOutput': selectedValues.value.step_2.constantLightOutput ? selectedValues.value.step_2.initialOutput: 0,
			'lifeTime': selectedValues.value.step_2.constantLightOutput ? selectedValues.value.step_2.lifeTime : '',
			'dimmingParameters': dimmingParameters,
			'mountingId': selectedValues.value.step_1.mounting ? selectedValues.value.step_1.mounting.id : null,
			'storeCode': storeCode,
		}

		const filter = {
			form_data: JSON.stringify(preparedData),
		};

		const { data } = await query<{ article: BaseAttribute }>(article, { filter });

		if (data.article && data.article.id !== '0') {
			selectedValues.value.general.article = data.article.id;
			selectedValues.value.general.inProcessOfLogin = false;

			getTenderText(apiUrl, storeCode);
		}

		let message = '';

		if (data.article.message.length > 0)  {
			if (data.article.message == 'unauthorized') {
				message = 'unauthorized';
			} else {
				if (data.article.message !== 'success') {
					message = data.article.message + ' - Sent by automatic error reporting, user was authorized and something went wrong. SessionId: ' + getSessionId(selectedValues.value.general.productSku.toString());
					createBugReportInAsana(message, true);
				}
			}
		}

		if (data) {
			loading.value.attributes = false;
		}
		loading.value.steps = false;

		return {
			article: data.article.id,
			message: message
		} ;
	};

	const getTenderText = async (apiUrl, storeCode) => {
		const preparedData = prepareTenderData(selectedValues.value, storeCode, productConfigurator.value.step_1.product.powerTemperature);

		axios({
			url: apiUrl + `tenderText/json/${storeCode}`,
			method: 'POST',
			data: preparedData,
			headers: {
				'Content-Type': 'application/json',
			},
		}).then((res) => {
			selectedValues.value.step_6.tenderText = res.data;
		}).catch((err) => {
			console.error(err);
		});
	}

	const prepareDimmingParameters = () => {
		switch (selectedValues.value.step_3.dimming.mn_label) {
			case 'M':

				return selectedValues.value.step_3.dimmingParameters.midnight.steps ?? [];
				break;

			case 'L':

				return selectedValues.value.step_3.dimmingParameters.mains ?? [];
				break;

			case 'S':

				return selectedValues.value.step_3.dimmingParameters.step ?? [];
				break;

			default:

				return [];
				break;
		}
	}

	const addHours = (numOfHours, date = new Date()) => {
		date.setTime(date.getTime() + numOfHours * 60 * 60 * 1000);

		return date;
	}

	const createBugReportInAsana = async (userMessage, sentWithSubmitButton) => {
		if (!selectedValues.value.general.asanaReportSent && sentWithSubmitButton) {
			const email = customerStore.user.email ?? 'Not logged in';

			sendAsanaBugReport(selectedValues.value, getSelectedLedModuleId(selectedValues.value, true), getProductVersionNames(selectedValues.value.step_1.productVersions), userMessage, sentWithSubmitButton, email).then(() => {
				selectedValues.value.general.asanaReportSent = true;
			});
		}
	}

	const updateSelectedValues = (step, attribute, value, cancelUpdate = false, selectedByUser = false) => {
		if (!cancelUpdate) {
			loading.value.attributes = true;
		}

		if (step === 'step_3') {
			selectedValues.value.general.reloadLuminaireConfigs = true;
		}

		if (selectedByUser) {
			updateUserSelectedAttributes(step, attribute);
		}

		if (selectedValues.value.general.editMode && importantStepsInEditMode.includes(step)) {
			selectedValues.value.general.chosenConfiguration = null;
			selectedValues.value.general.luminaireConfigs = [];
			selectedValues.value.general.filteredLuminaireConfigs = [];
		}

		valueIsChanged = true;

		selectedValues.value[step][attribute] = value;
		const formData = {};
		formData[step] = selectedValues.value[step];

		if (step !== 'general' ) {
			currentStep.value = step;
		}

		if (!cancelUpdate) {
			checkAttributesToUpdate(formData, attribute);
		}

		checkIfStepIsCompleted(step, false, selectedValues.value, productConfigurator.value, true);
	}

	const updateUserSelectedAttributes = (step, attribute) => {
		if (!selectedValues.value.general.attributesSelectedByUser.hasOwnProperty(step)) {
			selectedValues.value.general.attributesSelectedByUser[step] = [];
		}

		if (selectedValues.value.general.attributesSelectedByUser[step] && !selectedValues.value.general.attributesSelectedByUser[step].includes(attribute)) {
			selectedValues.value.general.attributesSelectedByUser[step].push(attribute);
		}
	}

	const checkAttributesToReset = (values, attribute) => {
		// Check if the colorTemperature object exists in the data
		let existingValues = [];

		if (selectedValues.value.step_2[attribute]) {
			existingValues = values.filter((item) => {
				// Check if the attribute matches, consider if the attribute is an object with id
				return item.id === selectedValues.value.step_2[attribute].id;
			});
		}

		if (existingValues.length === 0) {
			selectedValues.value.step_2[attribute] = null;
		}
	};


	const getPhotometricFilesAvailable = async (apiUrl) => {
		const preparedData = {
			source: 'C',
			type: 'ldt',
			product_id: selectedValues.value.general.beeProductId,
			diode_id: selectedValues.value.general.selectedDiode ?? 0,
			cri_id: Number(selectedValues.value.step_2.cri.id),
			color_temperature_id: Number(selectedValues.value.step_2.colorTemperature.id),
			led_module_id: Number(getSelectedLedModuleId(selectedValues.value)),
			lens_id: selectedValues.value.step_2.lens.id ?? 0,
			total_diodes: selectedValues.value.general.chosenConfiguration.total_diodes ?? 0,
			power: selectedValues.value.general.chosenConfiguration.power,
			flux: selectedValues.value.general.chosenConfiguration.flux,
			article: selectedValues.value.general.article ?? '',
			return_files_available: true,
		}

		try {
			const res = await axios({
				url: apiUrl + 'getPhotometricFile',
				method: 'POST',
				data: preparedData,
				headers: {
					'Content-Type': 'application/json',
				},
			});

			if (res.status === 200) {
				selectedValues.value.step_6.filesAvailable = res.data;
			} else {
				console.error('Could not find LDT file.');
			}
		} catch (error) {
			console.error('Error fetching photometric files:', error);
		}
	}

	const resetFormData = async (reload = true): Promise<any> => {
		productConfigurator.value.step_2.lenses = [];
		productConfigurator.value.step_2.cri = [];

		selectedValues.value.general = {
			activeStep: 'step_1',
			selectedDiode: null,
			availableDiodes: null,
			productSku: 0,
			beeProductId: 0,
			product: null,
			productName: '',
			productApprovals: '',
			reloadLuminaireConfigs: false,
			luminaireConfigs:[],
			filteredLuminaireConfigs: [],
			modelNamePatterns: [],
			modelName: [],
			ledQuantity: 0,
			chosenConfiguration: null,
			formIsComplete: false,
			attributeInfo: {},
			application: '',
			userHasChangedFilters: false,
			article: '',
			editMode: false,
			inProcessOfLogin: false,
			asanaReportSent: false,
			diode: null,
			productDescription: '',
			bodyMaterial: '',
			coverMaterial: '',
			opticMaterial: '',
			ip_rating: 0,
			ik_rating: 0,
			attributesSelectedByUser: {},
			unfinishedItems: [],
			formDataLogged: false,
			userStory: [],
		}

		selectedValues.value.step_1 = {
			productVersions: {},
			console: null,
			mounting: null,
			formIsLoaded: false,
			isComplete: false,
			versionsAreEdited: false,
			consolesLoaded: false,
		}

		selectedValues.value.step_2 = {
			formIsLoaded: false,
			lens: null,
			cri: null,
			colorTemperature: null,
			constantLightOutput: false,
			initialOutput: 0,
			lifeTime: '',
			fluxPower: {
				selected: 'flux',
				power: 0,
				flux: 0
			},
			minFlux: 0,
			maxFlux: 0,
			minPower: 0,
			maxPower: 0,
			originalMaxPower: 0,
			originalMaxFlux: 0,
			isComplete: false,
			typicalValues: {
				colorTemperature: [],
				cri: [],
			},
			LEDCategoryName: '',
		}

		selectedValues.value.step_3 = {
			insulation: null,
			surgeProtection: false,
			thermalProtectionNTC: false,
			driverSize: [],
			sockets: {},
			socketOption: null,
			dimming: null,
			isComplete: false,
			configCount: 0,
			scheduleIsSet: false,
			dimmingParameters: {
				midnight: {
					steps: {
						1: {
							outputLevel: 100,
							setTime: ''
						},
						2: {
							outputLevel: 50,
							setTime: {
								HH: '23',
								mm: '00',
							}
						},
						3: {
							outputLevel: 100,
							setTime: {
								HH: '05',
								mm: '00',
							}
						},
						4: {
							outputLevel: '',
							setTime: {
								HH: null,
								mm: null,
							}
						},
						5: {
							outputLevel: '',
							setTime: {
								HH: null,
								mm: null,
							}
						},
						6: {
							outputLevel: 0,
							setTime: null
						}
					}
				},
				step: {
					switchOnLevel: 50,
					SDLevel: 100,
				},
				mains: {
					minLevel: 50,
					maxLevel: 100,
					minVoltage: 180,
					maxVoltage: 230,
				}
			}
		}

		selectedValues.value.step_4 = {
			diodeQuantity: [],
			isComplete: false,
			ranks: [],
			binId: 0,
			powerFactor: 0,
			luminousEfficacy: 0,
			driverPowerUtilization: 70,
			diodeCurrent: 0,
			surgeProtectionLN: 6,
			surgeProtectionPE: 6,
			ledModuleTypes: [],
			maxLedsPerModule: 0,
			minLuminousEfficacy: 100,
			maxLuminousEfficacy: 150,
			luminaireConfigPreference: 1,
			preferenceChanged: false,
			activeFilters: {
				current: false,
				surgeProtection: true,
				driverRank: true,
				minPowerFactor: false,
				minLuminousEfficacy: false,
				driverPowerUtilization: true,
				diodeQuantity: false,
				ledModuleType: false,
			}
		}

		selectedValues.value.step_5 = {
			color: null,
			layer: null,
			mountingScrewLength: null,
			isComplete: false,
		}

		selectedValues.value.step_6 = {
			ldtFilePath: null,
			iesFilePath: null,
			filesAvailable: [],
			productImagePath: null,
			tenderText: '',
			dimensions: {},
			versionNumber:'',
			projectReference: '',
		}

		await saveFormData(selectedValues, '', '');

		if (reload) {
			location.reload();
		}
	}

	return {
		getProductConfigurator,
		getLenses,
		getColorTemperature,
		getFluxRange,
		updateSelectedValues,
		getStepThree,
		getLedModuleType,
		getStepFive,
		getLuminaireConfigFilters,
		resetFormData,
		testLuminaireConfigsAvailability,
		filterLuminaireConfigs,
		generateModelName,
		getLdtOrIesFile,
		filterUnavailableModuleTypes,
		getProductImage,
		reserveArticle,
		driverSize,
		createBugReportInAsana,
		getDiode,
		getTenderText,
		getConsoles,
		getDriverOptions,
		setLedModuleBasedOnConfig,
		getPhotometricFilesAvailable,
		productConfigurator,
		selectedValues,
		currentStep,
		initialLoad,
		loading
	};
}

export default useProductConfigurator;
